import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { 
  getPostBySlug, 
  getPostComments, 
  addComment,
  getRelatedPosts,
  BlogPost, 
  BlogComment 
} from '../services/blogService';
import { useAuth } from '../contexts/AuthContext';
import '../styles/BlogPostPage.scss';
import BlogFooter from '../components/BlogFooter';

// Social sharing components
const ShareButtons: React.FC<{ post: BlogPost }> = ({ post }) => {
  const url = window.location.href;
  const title = post.title;
  
  return (
    <div className="share-buttons">
      <h4>Share this article</h4>
      <div className="share-icons">
        <a 
          href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`} 
          target="_blank" 
          rel="noopener noreferrer"
          className="share-icon twitter"
          aria-label="Share on Twitter"
        >
          <i className="fab fa-twitter"></i>
        </a>
        <a 
          href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} 
          target="_blank" 
          rel="noopener noreferrer"
          className="share-icon facebook"
          aria-label="Share on Facebook"
        >
          <i className="fab fa-facebook-f"></i>
        </a>
        <a 
          href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} 
          target="_blank" 
          rel="noopener noreferrer"
          className="share-icon linkedin"
          aria-label="Share on LinkedIn"
        >
          <i className="fab fa-linkedin-in"></i>
        </a>
        <button 
          onClick={() => {
            navigator.clipboard.writeText(url);
            alert('Link copied to clipboard!');
          }} 
          className="share-icon copy"
          aria-label="Copy link"
        >
          <i className="fas fa-link"></i>
        </button>
      </div>
    </div>
  );
};

// Comment form component
const CommentForm: React.FC<{
  postId: string;
  parentId?: string;
  onCommentSubmit: () => void;
  onCancel?: () => void;
}> = ({ postId, parentId, onCommentSubmit, onCancel }) => {
  const { user } = useAuth();
  const [content, setContent] = useState('');
  const [guestName, setGuestName] = useState('');
  const [guestEmail, setGuestEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!content.trim()) {
      setError('Comment cannot be empty');
      return;
    }
    
    if (!user && (!guestName.trim() || !guestEmail.trim())) {
      setError('Name and email are required for guest comments');
      return;
    }
    
    setSubmitting(true);
    setError(null);
    
    try {
      const result = await addComment(
        postId,
        content,
        parentId,
        user?.id,
        !user ? guestName : undefined,
        !user ? guestEmail : undefined
      );
      
      if (result) {
        setContent('');
        setGuestName('');
        setGuestEmail('');
        onCommentSubmit();
      } else {
        setError('Failed to submit comment. Please try again.');
      }
    } catch (err) {
      setError('An error occurred while submitting your comment');
      console.error('Error submitting comment:', err);
    }
    
    setSubmitting(false);
  };

  return (
    <div className={`comment-form ${parentId ? 'reply-form' : ''}`}>
      <form onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="comment-content">Comment</label>
          <textarea
            id="comment-content"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Write your comment here..."
            rows={4}
            required
          ></textarea>
        </div>
        
        {!user && (
          <>
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="guest-name">Name</label>
                <input
                  id="guest-name"
                  type="text"
                  value={guestName}
                  onChange={(e) => setGuestName(e.target.value)}
                  placeholder="Your name"
                  required
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="guest-email">Email</label>
                <input
                  id="guest-email"
                  type="email"
                  value={guestEmail}
                  onChange={(e) => setGuestEmail(e.target.value)}
                  placeholder="Your email"
                  required
                />
              </div>
            </div>
            <p className="guest-note">
              {parentId ? 'Your reply' : 'Your comment'} will be reviewed before it appears.
            </p>
          </>
        )}
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="form-buttons">
          <button 
            type="submit" 
            className="submit-button"
            disabled={submitting}
          >
            {submitting ? 'Submitting...' : parentId ? 'Post Reply' : 'Post Comment'}
          </button>
          
          {parentId && onCancel && (
            <button 
              type="button" 
              className="cancel-button"
              onClick={onCancel}
            >
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

// Comment component
const Comment: React.FC<{
  comment: BlogComment;
  postId: string;
  onCommentSubmit: () => void;
}> = ({ comment, postId, onCommentSubmit }) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const { user } = useAuth();
  
  // Format comment date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // Get display name for comment author
  const getAuthorName = () => {
    if (comment.user?.first_name && comment.user?.last_name) {
      return `${comment.user.first_name} ${comment.user.last_name}`;
    } else if (comment.guest_name) {
      return comment.guest_name;
    } else {
      return 'Anonymous';
    }
  };
  
  return (
    <div className="comment">
      <div className="comment-avatar">
        {/* Use initials for avatar */}
        <div className="avatar-initials">
          {getAuthorName().split(' ').map(name => name[0]).join('').toUpperCase().slice(0, 2)}
        </div>
      </div>
      
      <div className="comment-content">
        <div className="comment-header">
          <div className="comment-author">{getAuthorName()}</div>
          <div className="comment-date">{formatDate(comment.created_at)}</div>
        </div>
        
        <div className="comment-text">{comment.content}</div>
        
        <div className="comment-actions">
          <button 
            className="reply-button"
            onClick={() => setShowReplyForm(!showReplyForm)}
          >
            {showReplyForm ? 'Cancel Reply' : 'Reply'}
          </button>
        </div>
        
        {showReplyForm && (
          <CommentForm 
            postId={postId} 
            parentId={comment.id} 
            onCommentSubmit={() => {
              onCommentSubmit();
              setShowReplyForm(false);
            }}
            onCancel={() => setShowReplyForm(false)}
          />
        )}
        
        {comment.replies && comment.replies.length > 0 && (
          <div className="comment-replies">
            {comment.replies.map(reply => (
              <div className="reply" key={reply.id}>
                <div className="comment-avatar">
                  <div className="avatar-initials">
                    {reply.user?.first_name && reply.user?.last_name 
                      ? `${reply.user.first_name[0]}${reply.user.last_name[0]}`.toUpperCase() 
                      : reply.guest_name 
                        ? reply.guest_name.split(' ').map(name => name[0]).join('').toUpperCase().slice(0, 2)
                        : 'AN'}
                  </div>
                </div>
                
                <div className="comment-content">
                  <div className="comment-header">
                    <div className="comment-author">
                      {reply.user?.first_name && reply.user?.last_name 
                        ? `${reply.user.first_name} ${reply.user.last_name}` 
                        : reply.guest_name || 'Anonymous'}
                    </div>
                    <div className="comment-date">{formatDate(reply.created_at)}</div>
                  </div>
                  
                  <div className="comment-text">{reply.content}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Floating Share Bar component 
const FloatingShareBar: React.FC<{ post: BlogPost }> = ({ post }) => {
  const url = window.location.href;
  const title = post.title;
  
  return (
    <div className="floating-share-bar">
      <a 
        href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`} 
        target="_blank" 
        rel="noopener noreferrer"
        className="share-icon twitter"
        aria-label="Share on Twitter"
      >
        <i className="fab fa-twitter"></i>
      </a>
      <a 
        href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} 
        target="_blank" 
        rel="noopener noreferrer"
        className="share-icon facebook"
        aria-label="Share on Facebook"
      >
        <i className="fab fa-facebook-f"></i>
      </a>
      <a 
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`} 
        target="_blank" 
        rel="noopener noreferrer"
        className="share-icon linkedin"
        aria-label="Share on LinkedIn"
      >
        <i className="fab fa-linkedin-in"></i>
      </a>
      <button 
        onClick={() => {
          navigator.clipboard.writeText(url);
          alert('Link copied to clipboard!');
        }} 
        className="share-icon copy"
        aria-label="Copy link"
      >
        <i className="fas fa-link"></i>
      </button>
    </div>
  );
};

// Main blog post page component
const BlogPostPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [post, setPost] = useState<BlogPost | null>(null);
  const [comments, setComments] = useState<BlogComment[]>([]);
  const [relatedPosts, setRelatedPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [tableOfContents, setTableOfContents] = useState<{ id: string; text: string; level: number }[]>([]);
  const [activeHeadingId, setActiveHeadingId] = useState<string | null>(null);
  const [readPercentage, setReadPercentage] = useState(0);
  const tocGeneratedRef = useRef(false);
  
  // Fetch post and comments
  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const postData = await getPostBySlug(slug);
        
        if (!postData) {
          setError('Post not found');
          setLoading(false);
          return;
        }
        
        setPost(postData);
        
        // Fetch comments
        const commentsData = await getPostComments(postData.id);
        setComments(commentsData);
        
        // Fetch related posts
        const related = await getRelatedPosts(postData.id, 3);
        setRelatedPosts(related);
      } catch (err) {
        console.error('Error fetching post:', err);
        setError('Failed to load the post');
      }
      
      setLoading(false);
      tocGeneratedRef.current = false;
    };
    
    fetchPost();
  }, [slug]);
  
  // Scroll to top when post changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  
  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // Handle comment submission
  const handleCommentSubmit = async () => {
    if (!post) return;
    
    // Refresh comments
    const commentsData = await getPostComments(post.id);
    setComments(commentsData);
  };
  
  // Generate table of contents from post content
  const generateTOC = useCallback(() => {
    if (!post || tocGeneratedRef.current) return;

    const headings: { id: string; text: string; level: number }[] = [];
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = post.content;
    
    // Find all headings (h2, h3)
    const headingElements = tempDiv.querySelectorAll('h2, h3');
    
    headingElements.forEach((el, index) => {
      const id = `heading-${index}`;
      if (!el.id) {
        el.id = id;
      }
      headings.push({
        id: el.id,
        text: el.textContent || '',
        level: el.tagName === 'H2' ? 2 : 3
      });
    });
    
    // Update post content with IDs and set TOC state
    if (headings.length > 0) {
      setPost(prev => prev ? { ...prev, content: tempDiv.innerHTML } : null);
      setTableOfContents(headings);
      tocGeneratedRef.current = true;
    }
  }, [post]);
  
  // Effect to generate TOC after post is loaded
  useEffect(() => {
    if (post && contentRef.current) {
      generateTOC();
    }
  }, [post, generateTOC]);

  // Effect for scroll-based features (Progress Bar & TOC Highlighting)
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return;

      const contentTop = contentRef.current.offsetTop;
      const contentHeight = contentRef.current.scrollHeight;
      const viewportHeight = window.innerHeight;
      const scrollY = window.scrollY;

      // Calculate Read Percentage
      const scrollableHeight = contentHeight - viewportHeight + contentTop;
      const currentScroll = scrollY - contentTop;
      let percentage = 0;
      if (scrollableHeight > 0 && currentScroll > 0) {
        percentage = Math.min(100, Math.max(0, (currentScroll / scrollableHeight) * 100));
      } else if (currentScroll >= scrollableHeight) {
        percentage = 100;
      }
      setReadPercentage(percentage);

      // Calculate Active Heading for TOC
      let currentActiveHeadingId: string | null = null;
      const offset = viewportHeight * 0.2; // Offset from top to activate heading

      tableOfContents.forEach((heading) => {
        const element = document.getElementById(heading.id);
        if (element) {
          const elementTop = element.getBoundingClientRect().top;
          if (elementTop >= 0 && elementTop < viewportHeight * 0.8) {
            if (currentActiveHeadingId === null || elementTop < offset) {
               currentActiveHeadingId = heading.id;
            }
          }
        }
      });
       setActiveHeadingId(currentActiveHeadingId);
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [tableOfContents]);
  
  // Scroll to heading
  const scrollToHeading = (id: string) => {
    if (contentRef.current) {
      const heading = contentRef.current.querySelector(`#${id}`);
      if (heading) {
        heading.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  };
  
  // Function to determine if content is markdown
  const isMarkdownContent = (content: string): boolean => {
    // Check for common markdown patterns
    const markdownPatterns = [
      /^#+ /, // Headers
      /\*\*.+\*\*/, // Bold
      /\*.+\*/, // Italic
      /\[.+\]\(.+\)/, // Links
      /```[\s\S]+```/, // Code blocks
      /^\s*[-*+] /, // Lists
      /^\s*\d+\. /, // Numbered lists
      /^\s*>/ // Blockquotes
    ];

    return markdownPatterns.some(pattern => pattern.test(content));
  };
  
  if (loading) {
    return (
      <div className="blog-post-loading">
        <div className="loading-spinner"></div>
        <p>Loading article...</p>
      </div>
    );
  }
  
  if (error || !post) {
    return (
      <div className="blog-post-error">
        <p>Sorry, we couldn't find the article you're looking for.</p>
        <Link to="/blog" className="back-link">Back to Blog</Link>
      </div>
    );
  }
  
  return (
    <div className="blog-post-page">
      <Helmet>
        <title>{post.meta_title || post.title} | PLAB2 Simulator</title>
        <meta name="description" content={post.meta_description || post.summary} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.summary} />
        <meta property="og:type" content="article" />
        {post.featured_image && <meta property="og:image" content={post.featured_image} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={post.summary} />
        {post.featured_image && <meta name="twitter:image" content={post.featured_image} />}
        <meta name="article:published_time" content={post.published_at || post.created_at} />
        <meta name="article:modified_time" content={post.updated_at} />
        {post.category && <meta name="article:section" content={post.category.name} />}
        {post.tags && post.tags.map(tag => (
          <meta key={tag.id} name="article:tag" content={tag.name} />
        ))}
      </Helmet>
      
      {/* Add Read Progress Bar */}
      <div className="read-progress-bar-container">
        <div className="read-progress-bar" style={{ width: `${readPercentage}%` }}></div>
      </div>
      
      {/* Add floating share bar */}
      <FloatingShareBar post={post} />
      
      <div className="blog-post-content-container">
        <div className="blog-post-header">
          <h1 className="post-title">{post.title}</h1>
          
          <div className="post-meta">
            <div className="post-author">
              {post.author && (
                <>
                  <div className="author-avatar">
                    <div className="avatar-initials">
                      {post.author.first_name && post.author.last_name 
                        ? `${post.author.first_name[0]}${post.author.last_name[0]}`.toUpperCase()
                        : 'A'}
                    </div>
                  </div>
                  <span>By {post.author.first_name} {post.author.last_name}</span>
                </>
              )}
            </div>
            
            <div className="post-info">
              <span className="post-date">
                <i className="far fa-calendar-alt"></i>
                {formatDate(post.published_at || post.created_at)}
              </span>
              
              <span className="post-reading-time">
                <i className="far fa-clock"></i>
                {Math.ceil(post.content.split(' ').length / 500)} min read
              </span>
            </div>
          </div>
          
          {post.featured_image && (
            <div className="post-featured-image">
              <img src={post.featured_image} alt={post.title} />
            </div>
          )}
        </div>
        
        <div className="blog-layout-container">
          <div className="blog-main-content">
            <article className="blog-post-article">
              <div 
                ref={contentRef}
                className="blog-post-body"
              >
                {post && isMarkdownContent(post.content) ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {post.content}
                  </ReactMarkdown>
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: post?.content || '' }} />
                )}
              </div>
              
              {post.tags && post.tags.length > 0 && (
                <div className="blog-post-tags">
                  <span className="tags-label">Tags:</span>
                  {post.tags.map(tag => (
                    <Link 
                      key={tag.id} 
                      to={`/blog/tag/${tag.slug}`}
                      className="tag"
                    >
                      {tag.name}
                    </Link>
                  ))}
                </div>
              )}
              
              <div className="blog-post-author-bio">
                <div className="author-avatar">
                  <div className="avatar-initials">
                    {post.author?.first_name && post.author?.last_name 
                      ? `${post.author.first_name[0]}${post.author.last_name[0]}`.toUpperCase()
                      : 'A'}
                  </div>
                </div>
                <div className="author-info">
                  <h4>
                    {post.author?.first_name && post.author?.last_name 
                      ? `${post.author.first_name} ${post.author.last_name}`
                      : 'Author'}
                  </h4>
                  <p>Best-graduating Doctor, Author and Founder of SimPLAB2. He also created Air-Clinic, a community helping doctors pass medical exams since 2020...</p>
                </div>
              </div>
            </article>
            
            <div className="blog-post-comments">
              <h3>Comments ({comments.length})</h3>
              
              <CommentForm 
                postId={post.id} 
                onCommentSubmit={handleCommentSubmit} 
              />
              
              {comments.length === 0 ? (
                <div className="no-comments">
                  <p>No comments yet. Be the first to share your thoughts!</p>
                </div>
              ) : (
                <div className="comments-list">
                  {comments.map(comment => (
                    <Comment 
                      key={comment.id} 
                      comment={comment} 
                      postId={post.id}
                      onCommentSubmit={handleCommentSubmit}
                    />
                  ))}
                </div>
              )}
            </div>
            
            {relatedPosts.length > 0 && (
              <div className="related-posts">
                <h3>Related Articles</h3>
                <div className="related-posts-grid">
                  {relatedPosts.map(relatedPost => (
                    <article className="related-post-card" key={relatedPost.id}>
                      <Link to={`/blog/${relatedPost.slug}`} className="related-post-image">
                        {relatedPost.featured_image ? (
                          <img src={relatedPost.featured_image} alt={relatedPost.title} />
                        ) : (
                          <div className="placeholder-image">
                            <span>PLAB2 Simulator</span>
                          </div>
                        )}
                      </Link>
                      
                      <div className="related-post-content">
                        <h3 className="post-title">
                          <Link to={`/blog/${relatedPost.slug}`}>{relatedPost.title}</Link>
                        </h3>
                        
                        <div className="post-date">
                          {formatDate(relatedPost.published_at || relatedPost.created_at)}
                        </div>
                      </div>
                    </article>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          <div className="blog-sidebar">
            <div className="sidebar-sticky-content">
              {/* CTA Section - Now placed first in sidebar */}
              <div className="sidebar-widget cta-section">
                <h3>Try Our PLAB2 Simulator</h3>
                <p>Prepare for your exam with realistic patient scenarios and get instant feedback.</p>
                <div className="cta-features">
                  <div className="cta-feature">
                    <i className="fas fa-user-md"></i>
                    <span>Realistic Simulations</span>
                  </div>
                  <div className="cta-feature">
                    <i className="fas fa-comments"></i>
                    <span>Interactive Conversations</span>
                  </div>
                  <div className="cta-feature">
                    <i className="fas fa-chart-line"></i>
                    <span>Performance Tracking</span>
                  </div>
                </div>
                <Link to="/" className="cta-button">Start Practicing Now</Link>
              </div>
              
              {/* Table of Contents - Now second */}
              {tableOfContents.length > 0 && (
                <div className="sidebar-widget table-of-contents">
                  <h3>Table of Contents</h3>
                  <ul>
                    {tableOfContents.map(heading => (
                      <li
                        key={heading.id}
                        className={`toc-level-${heading.level} ${activeHeadingId === heading.id ? 'active' : ''}`}
                      >
                        <button onClick={() => scrollToHeading(heading.id)}>
                          {heading.text}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {/* Share Buttons - Now third */}
              <div className="sidebar-widget share-buttons">
                <h3>Share this article</h3>
                <div className="share-icons">
                  <a 
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(window.location.href)}&text=${encodeURIComponent(post.title)}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="share-icon twitter"
                    aria-label="Share on Twitter"
                  >
                    <i className="fab fa-twitter"></i>
                  </a>
                  <a 
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="share-icon facebook"
                    aria-label="Share on Facebook"
                  >
                    <i className="fab fa-facebook-f"></i>
                  </a>
                  <a 
                    href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(window.location.href)}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="share-icon linkedin"
                    aria-label="Share on LinkedIn"
                  >
                    <i className="fab fa-linkedin-in"></i>
                  </a>
                  <button 
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.href);
                      alert('Link copied to clipboard!');
                    }} 
                    className="share-icon copy"
                    aria-label="Copy link"
                  >
                    <i className="fas fa-link"></i>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className="blog-cta-banner">
        <div className="cta-content">
          <h2>Ready to Excel in Your PLAB2 Exam?</h2>
          <p>Join thousands of medical professionals who have improved their clinical skills with our simulator.</p>
          <div className="cta-features">
            <div className="cta-feature">
              <i className="fas fa-check-circle"></i>
              <span>Realistic Patient Scenarios</span>
            </div>
            <div className="cta-feature">
              <i className="fas fa-check-circle"></i>
              <span>Instant Feedback</span>
            </div>
            <div className="cta-feature">
              <i className="fas fa-check-circle"></i>
              <span>Progress Tracking</span>
            </div>
          </div>
          <Link to="/" className="cta-button">Try PLAB2 Simulator Now</Link>
        </div>
      </div>
      
      <BlogFooter />
    </div>
  );
};

export default BlogPostPage; 